import h from 'hyperscript';

import { ariaBoolean } from 'cadenza/utils/aria';
import { uniqueId } from 'cadenza/utils/unique-id';
import type { EventKey } from 'cadenza/utils/event-util';
import { ChangeEvent, on, unByKey } from 'cadenza/utils/event-util';

const COMPONENT_NAME = 'd-radio-group';

export interface LabelledValue {
  label: string | Node;
  value: string;
  disabled?: boolean;
}

export interface RadioGroupOptions {
  initialValue?: string;
  inline?: boolean;
  name?: string;
  required?: boolean;
  ariaLabelledBy?: string;
}

export class RadioGroup extends HTMLElement {

  #initialValue;
  #initiallyRequired;
  #values;
  #inline;
  #name;
  #ariaLabelledBy;
  #labelEventKey?: EventKey;

  /**
   * @param values - List of values with labels
   * @param [options] - Options
   * @param [options.initialValue] - The initial value, default is the first value
   * @param [options.inline] - Should radio buttons be displayed inline, default is false
   * @param [options.name] - Radio group name, default is an auto-generated unique ID
   * @param [options.required] - Whether the radio group is initially required, default is false
   * @param [options.ariaLabelledBy] - The ID of this radio group's label (no default value)
   */
  constructor (values: LabelledValue[], {
    initialValue = values[0].value,
    inline = false,
    name = uniqueId(),
    required = false,
    ariaLabelledBy
  }: RadioGroupOptions = {}) {
    super();
    this.#initialValue = initialValue;
    this.#initiallyRequired = required || false;
    this.#values = values;
    this.#inline = inline || false;
    this.#name = name;
    this.#ariaLabelledBy = ariaLabelledBy;
    this.addEventListener('change', (event) => {
      if (event.target !== this) {
        event.stopImmediatePropagation();
        this.dispatchEvent(new ChangeEvent());
      }
    });
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME, 'form-group-control');
    this.setAttribute('role', 'radiogroup');
    this.append(...this.#values.map(value => this._createRadioOption(value)));
    this.required = this.#initiallyRequired;

    if (this.#ariaLabelledBy) {
      this.setAttribute('aria-labelledby', this.#ariaLabelledBy);
    }

    let labelledBy = this.getAttribute('aria-labelledby');
    if (labelledBy) {
      const label = document.getElementById(labelledBy);
      if (label) {
        this.#labelEventKey = on(label, 'click', () => this._checkedRadio?.focus());
      }
    }
  }

  _createRadioOption ({ value, label, disabled }: LabelledValue) {
    return h('label.d-form-check'
        + (this.#inline ? '.d-form-check-inline' : '')
        + ((this.disabled || disabled) ? '.disabled' : ''), [
      h('input', {
        type: 'radio',
        name: this.#name,
        value,
        checked: value === this.#initialValue,
        disabled: this.disabled || disabled
      }),
      label
    ]);
  }

  disconnectedCallback () {
    unByKey(this.#labelEventKey);
    this.#initialValue =
      this.value; // in case this components is (dis)connected multiple time, keep checked state
    this.innerHTML = '';
  }

  get value () {
    return this._checkedRadio?.value;
  }

  set value (value) {
    const input = this.querySelector(`input[value="${value}"]`) as HTMLInputElement;
    if (input) {
      input.checked = true;
    }
  }

  get _checkedRadio () {
    return this.querySelector('input:checked') as HTMLInputElement;
  }

  get disabled () {
    return ariaBoolean(this, 'disabled');
  }

  set disabled (value) {
    ariaBoolean(this, 'disabled', value);
    this.querySelectorAll('input').forEach(input => { input.disabled = value; });
  }

  get required () {
    return ariaBoolean(this, 'required');
  }

  set required (value) {
    ariaBoolean(this, 'required', value);
    this.querySelectorAll('input').forEach(input => { input.required = value; });
  }

}

customElements.define(COMPONENT_NAME, RadioGroup);
